import React from 'react';
import { has, isEmpty, isEqual, map, toInteger, trim, values } from 'lodash';
import { Link } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import PipelinesListProjectHeader from 'new/components/pipelines/PipelinesListProjectHeader';
import {
  AvailableFilterTypesByView,
  DefaultFiltersByView,
  SearchFilterTypes,
  SearchFilterViews,
} from 'js/constants/visibilityFilters';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { Folder as FolderIcon } from '@mui/icons-material';
import { POLLING_PRESET_INTERVALS } from 'new/constants/pollingConfig';
import Constants from 'js/Constants';
import SearchBar from 'js/components/MacroInput/SearchBar';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import Button from 'js/components/elements/editors/mui/Button';
import PipelinesList from 'js/components/pipelines/PipelinesList';
// import ModalProjectActionContainer from 'js/components/projects/ModalProjectActionContainer';
import ProjectDetailViewContainer from 'js/components/projects/cards/ProjectDetailViewContainer';
import {
  PROJECT_DETAIL_VIEW,
  PROJECT_DETAIL_VIEW_ACCESS_TAB,
  TOGGLE_PROJECT_PIPELINES,
} from 'js/constants/projects';
import { SUPPORT_KEY_FLOW_LIST } from 'js/constants/supportPageKeys';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import Projects from 'new/components/pipelines/ProjectsList';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { formatAll, resultToComplex } from '../MacroInput/tools/macroTools';
import { segmentsToString } from '../MacroInput/tools/segmentsToString';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../elements/rselect/ReactSelectUtils';
import UnsortedSelect from '../elements/rselect/UnsortedSelect';
import ModalCloneFlowContainer from './ModalCloneFlowContainer';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';
import { FLOW_METRICS_INTERVAL_LATEST_RUN } from 'js/constants/metricsConstants';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import ExpandedPipelines from 'js/components/pipelines/ExpandedPipelines';
import { ExpandController } from 'js/components/pipelines/ExpandController';
import PipelinesEmptyListGuide from 'new/components/pipelines/PipelinesEmptyList/PipelinesEmptyListGuide';
import EmptyProjectState from 'new/components/pipelines/EmptyProjectState';
import EmptySqlAutomationsState from 'new/components/pipelines/EmptySqlAutomationsState/EmptySqlAutomationsState';
import { EmptyMCPFlowsState } from 'new/components/mcpFlows/EmptyMCPFlowsState';
import ProjectFlowsView from 'js/components/pipelines/ProjectFlowsView';
import { BulkActionsBar } from 'new/components/pipelines/bulkActions';

import './Pipelines.scss';

export const IntegrationDescription = (props) => {
  return (
    <Typography {...props}>
      Visualize the journey of your data from its origin to its destination. Choose any{' '}
      <KnowledgePanel
        alwaysShowUnderline
        knowledgeContent="Flow Origin refers to the start of the Data Flow, e.g., the Data Source."
      >
        Flow Origin
      </KnowledgePanel>{' '}
      to reveal all associated flows. Explore resource details and their connections both upstream
      and downstream.
    </Typography>
  );
};

const styles = {
  overflowHidden: { overflow: 'hidden' },
  flex: {
    display: 'flex',
  },
  flexGrow: {
    flexGrow: 1,
  },
  mainContent: {
    paddingRight: 8,
  },
  titleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'hsl(202,87%,12%)',
    paddingTop: 14,
    paddingBottom: 14,
    margin: 0,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderRadius: 0,
  },
  textArea: {
    display: 'flex',
    flexDirection: 'column',
  },
  heading: {
    paddingLeft: 20,
    marginBottom: 0,
    marginTop: 0,
    fontSize: 20,
    color: '#ff7436',
  },
  subheading: {
    fontSize: 12,
    paddingLeft: 20,
    color: '#cccccc',
  },
  headingCTA: {
    width: 170,
    marginRight: 24,
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: 12,
  },
  projectIcon: { fontSize: 32, marginRight: 8, color: '#357bff' },
};

const selectStyles = {
  ...baseSelectStyles,
  control: (styles) => ({
    ...styles,
    minHeight: 38,
    borderRadius: 8,
  }),
  container: (styles) => ({
    ...styles,
    width: 165,
    height: 38,
    fontSize: 12,
    marginRight: 8,
  }),
  option: (styles) => ({
    ...styles,
    padding: '0 12px',
    fontSize: 12,
    lineHeight: '32px',
  }),
};

const sortCriteriaOptions = values(Constants.SortCriteriaInfo);

function resolveSearchFilterView({ isProjectPipelinesView, isSQLAutomations, isMCPFlows }) {
  if (isProjectPipelinesView) return SearchFilterViews.PROJECT_PIPELINES;
  if (isSQLAutomations) return SearchFilterViews.SQL_AUTOMATIONS;
  if (isMCPFlows) return SearchFilterViews.MCP_FLOWS;
  return SearchFilterViews.PIPELINES;
}

class Pipelines extends React.PureComponent {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    const urlParamsInitial = new URLSearchParams(props.history.location.search);
    const pageParamInitial = toInteger(urlParamsInitial.get('page'));
    this.state = {
      showExpandPipelinesForProject: false,
      expanded: props.useExpanded,
      scaleFactor: 1,
      selectedSortOption: getCurrentSelectedOption(sortCriteriaOptions, props.sortCriteria),
      currentPage:
        pageParamInitial && pageParamInitial !== 0 ? pageParamInitial : props.currentPage,
      selectedDataSource: props.selectedFlow,
      assignedFirstDataSource: false,
      selectedProject: { id: null, type: null },
      cloneSelectedFlow: null,
      isGenAiChatOpen: false,
      isPageLoading: false,
      selectedFlowIds: new Set(),
    };

    this._setTimer = this._setTimer.bind(this);
    this._clearTimer = this._clearTimer.bind(this);
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    this._handleScaleFactorChanged = this._handleScaleFactorChanged.bind(this);
    this._handleSourceClicked = this._handleSourceClicked.bind(this);
    this._handleProjectClicked = this._handleProjectClicked.bind(this);

    this._handleSearchSuggestionSelected = this._handleSearchSuggestionSelected.bind(this);
    this._handleSortCriteriaChanged = this._handleSortCriteriaChanged.bind(this);
    this._handleCloseSourcePipeline = this._handleCloseSourcePipeline.bind(this);
    this._handleRefreshPipeline = this._handleRefreshPipeline.bind(this);
    this._handleTick = this._handleTick.bind(this);
    this._loadPipelines = this._loadPipelines.bind(this);
    this._getProjectFlows = this._getProjectFlows.bind(this);
    this._getFlowObj = this._getFlowObj.bind(this);
    this._toggleCloneFlowModal = this._toggleCloneFlowModal.bind(this);
    this._setSelectedPipeline = this._setSelectedPipeline.bind(this);
    this._loadMetrics = this._loadMetrics.bind(this);
    // }

    // componentDidMount() {
    // moved this code from componentDidMount to the constructor so that
    // updateSearchFilters() can be called before SearchBar is mounted and its
    // initial value can be set to the search query in the URL

    this._defaultFilters = trim(
      segmentsToString(DefaultFiltersByView[SearchFilterViews.PIPELINES]),
    );

    const { history, match, updateSearchFilters, setCurrentPage } = this.props;
    const { selectedDataSource } = this.state;
    const urlParams = new URLSearchParams(history.location.search);
    const pageParam = toInteger(urlParams.get('page'));
    const searchParam = urlParams.get('search');
    const isCustomSearchParam =
      searchParam &&
      trim(segmentsToString(resultToComplex(formatAll(searchParam)).segments)) !==
        this._defaultFilters;

    this.props.resetPageNumbers();
    this.props.resetSortCriteria();
    if (pageParam || isCustomSearchParam) {
      if (searchParam) {
        updateSearchFilters(
          match?.params?.projectId
            ? SearchFilterViews.PROJECT_PIPELINES
            : SearchFilterViews.PIPELINES,
          resultToComplex(formatAll(searchParam)).segments,
        );
      }

      if (pageParam) {
        setCurrentPage(pageParam);
      }
    }

    this._loadPipelines().then(() => {
      setTimeout(() => {
        const index = this.props.pipelines.findIndex(
          (pipeline) =>
            pipeline &&
            pipeline.flow_node_id === (this.state.selectedDataSource || this.props.selectedFlow),
        );
        if (index !== -1 && this.listRef) {
          this.scrollToIndex(index);
        }
      }, 500);
    });

    if (selectedDataSource) {
      this._setTimer();
    }

    this.props.setContextualSupport(SUPPORT_KEY_FLOW_LIST);
  }

  componentDidMount() {
    const index = this.props.pipelines.findIndex(
      (pipeline) => pipeline && pipeline.flow_node_id === this.props.selectedFlow,
    );
    if (index !== -1 && this.listRef) {
      this.scrollToIndex(index);
    }

    this._refreshProject();

    // Add visibility change listener for smart polling (pause when tab is hidden)
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      sortCriteria,
      filters,
      accessControl,
      lastAddedSourceId,
      lastAddedDataSetId,
      lastAddedSinkId,
      lastLoaded,
      refreshPipeline,
      loadingRemoveFromProject,
      loadingDeleteProject,
      match,
      flowOperationRequestFlags,
    } = this.props;

    const { selectedDataSource, selectedSortOption, currentPage } = this.state;

    (() => {
      if (prevProps.sortCriteria === sortCriteria) return;
      // If detail view is opened, do not set sortCriteria
      if (this.props.selectedCardID) return;
      this.setState({
        selectedSortOption: getCurrentSelectedOption(sortCriteriaOptions, sortCriteria),
      });
    })();

    if (match?.params?.projectId && match.params.projectId !== prevProps.match?.params?.projectId) {
      this._loadPipelines().then(() => {
        if (!this.state.assignedFirstDataSource) {
          this.setState({
            selectedDataSource: this.props.pipelines?.[0]?.flow_node_id,
            assignedFirstDataSource: true,
          });
        }
      });
    }

    const accessLevelChanged = prevProps.accessControl.mode !== accessControl.mode;
    if (accessLevelChanged) {
      // Sync local page state with Redux (which was reset to 1 by the ACL toggle handler)
      // so that CDU correctly detects future page changes via the pagination controls.
      this.setState({ currentPage: this.props.currentPage, selectedFlowIds: new Set() });
      this._loadPipelines().catch(console.error);
    }

    // if a new source or Nexset is added (by copying an existing one), reload pipeline
    else if (
      prevProps.lastAddedDataSetId !== lastAddedDataSetId ||
      prevProps.lastAddedSinkId !== lastAddedSinkId
    ) {
      let flowNodeId;
      if (prevProps.lastAddedDataSetId !== lastAddedDataSetId) {
        const dataSet = this.props.entities.dataSets[lastAddedDataSetId];
        flowNodeId = dataSet.origin_node_id;
      } else {
        const sink = this.props.entities.sinks[lastAddedSinkId];
        flowNodeId = sink.origin_node_id;
      }
      refreshPipeline(flowNodeId);
    } else if (
      prevState.currentPage !== currentPage ||
      prevState.selectedSortOption !== selectedSortOption ||
      (areSegmentsDifferent(this.props.filters, prevProps.filters) &&
        !this.props.isLoadingPipelines)
    ) {
      if (!this.props.selectedCardID) {
        // This means we are NOT changing sortCriteria or currentPage from the flow link selection
        const isPageChange = prevState.currentPage !== currentPage;
        if (isPageChange) this.setState({ isPageLoading: true });
        this._loadPipelines().then(() => {
          if (isPageChange) this.setState({ isPageLoading: false });
        });
        this.props.saveHashedState('flows.viewport.flow_nodes_all', null);
      }
    } else if (prevProps.lastAddedSourceId !== lastAddedSourceId) {
      // Skip navigation if form modal is open to avoid unmounting during source creation
      if (this.props.activeViews?.formViewOpen) return;

      if (!isEmpty(filters)) {
        if (this.props.lastAddedSource.id === lastAddedSourceId) {
          refreshPipeline(this.props.lastAddedSource.flow_node_id).then(() => {
            this.setState({
              selectedDataSource: this.props.lastAddedSource.flow_node_id,
            });
          });
        } else {
          this.props.history.push(
            `${this.context}${ROUTE_PIPELINES}?search=${SearchFilterTypes.SOURCE_ID}:${lastAddedSourceId}`,
          );
        }
      } else {
        this._loadPipelines().then(() => {
          if (!this.state.assignedFirstDataSource) {
            this.setState({
              selectedDataSource: this.props.pipelines?.[0]?.flow_node_id,
              assignedFirstDataSource: true,
            });
          }
        });
      }
    }

    if (
      selectedDataSource &&
      (prevProps.lastLoaded !== lastLoaded || selectedDataSource !== prevState.selectedDataSource)
    ) {
      this._setTimer();
    }

    if (
      !isEqual(prevState.selectedDataSource, this.state.selectedDataSource) &&
      !!this.state.selectedDataSource
    ) {
      this.props.refreshPipeline(this.state.selectedDataSource);
      this.scrollToIndex(0);
    }

    (() => {
      if (loadingRemoveFromProject === prevProps.loadingRemoveFromProject) return;
      if (loadingRemoveFromProject) return;
      if (!match?.params?.projectId) return;
      this._loadPipelines();
    })();

    (() => {
      if (loadingDeleteProject === prevProps.loadingDeleteProject) return;
      if (loadingDeleteProject) return;
      if (!match?.params?.projectId) return;
      this.props.history.replace(Projects.screenConfig.path);
    })();

    (() => {
      if (!this.props.selectedFlow) return;
      if (prevProps.selectedFlow === this.props.selectedFlow) return;
      const index = this.props.pipelines.findIndex(
        (pipeline) => pipeline && pipeline.flow_node_id === this.props.selectedFlow,
      );
      if (index !== -1 && this.listRef) {
        this.scrollToIndex(index);
      }
      this.setState({
        selectedDataSource: this.props.selectedFlow,
      });
    })();

    (() => {
      if (this.props.useExpanded === undefined) return;
      if (this.state.expanded !== undefined) return;
      if (prevProps.useExpanded === this.props.useExpanded) return;
      this.setState({
        expanded: this.props.useExpanded,
      });
    })();

    // Clear bulk selection when the visible set of flows changes
    if (
      prevState.currentPage !== this.state.currentPage ||
      prevProps.match?.params?.projectId !== this.props.match?.params?.projectId ||
      areSegmentsDifferent(this.props.filters, prevProps.filters)
    ) {
      this._clearFlowSelection();
    }

    // Check for specific flow-related operations that affect project flow counts
    this._checkForFlowOperations(prevProps);
  }

  componentWillUnmount() {
    this._clearTimer();
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    this.props.unsetContextualSupport(SUPPORT_KEY_FLOW_LIST);
  }

  render() {
    const {
      pipelines,
      isLoadingPipelines,
      isRefreshingPipeline,
      getSourceMeta,
      match,
      withHeader,
      disableProjectView,
      filters,
      projectById,
      isLoadingProjectById,
      isSQLAutomations,
      isMCPFlows,
      onLoadPipelinesSearchSuggestions,
    } = this.props;
    const { selectedSortOption, currentPage, showExpandPipelinesForProject } = this.state;

    const projects = this.getProjectsEntities();
    const selectedProject = this.getProjectContext();
    const isProjectPipelinesView = !!selectedProject;

    const currentFilters = trim(segmentsToString(filters));
    // Show skeleton during active fetches (not background refresh).
    // After loading completes, noContentView handles empty state.
    const isLoading = (isLoadingPipelines && !isRefreshingPipeline) || this.state.isPageLoading;

    const flowsCount = projectById?.[match?.params?.projectId]?.count || 0;

    const searchFilterView = resolveSearchFilterView({
      isProjectPipelinesView,
      isSQLAutomations,
      isMCPFlows,
    });

    const SearchBarComponent = (
      <MacroOptionsContextWrap>
        <SearchBar
          ref={(ref) => (this.searchBar = ref)}
          view={searchFilterView}
          availableFilterTypes={AvailableFilterTypesByView[searchFilterView]}
          getSourceMeta={getSourceMeta}
          onResourceSelected={this._handleSearchSuggestionSelected}
          onLoadPipelinesSearchSuggestions={onLoadPipelinesSearchSuggestions}
        />
      </MacroOptionsContextWrap>
    );

    const SortSelect = (
      <UnsortedSelect
        options={sortCriteriaOptions}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        isSearchable={true}
        backspaceRemovesValue={false}
        value={selectedSortOption}
        onChange={this._handleSortCriteriaChanged}
        styles={selectStyles}
      />
    );

    const Pagination = (
      <CardsListPageNumbersContainer
        page={currentPage}
        onChange={(page) => this.setState({ currentPage: page })}
        isLoading={isLoadingPipelines}
      />
    );

    let noContentView = null;
    let projectIdForNoContentView = match?.params?.projectId;

    if (isEmpty(pipelines) && !isLoadingPipelines) {
      if (currentFilters && currentFilters !== this._defaultFilters) {
        noContentView = (
          <NoContentTextView title={<div>No results match your search criteria.</div>} />
        );
      } else if (!!projectIdForNoContentView) {
        noContentView = (
          <EmptyProjectState
            projectId={Number(projectIdForNoContentView)}
            onCreateFlowClick={() => {
              const flows = this.props.pipelines || [];
              if (flows.length === 0) {
                // Set currentProjectId synchronously to avoid race before navigation
                this.props.setCurrentProjectId(Number(projectIdForNoContentView));
              }
            }}
          />
        );
      } else if (isSQLAutomations) {
        noContentView = <EmptySqlAutomationsState />;
      } else if (isMCPFlows) {
        noContentView = <EmptyMCPFlowsState />;
      } else {
        noContentView = <PipelinesEmptyListGuide />;
      }
    }

    return (
      <div>
        {withHeader !== false && (
          <Paper elevation={1} style={styles.titleRoot}>
            <div style={styles.textArea}>
              <h3 style={styles.heading}>Data Flows</h3>
              <div style={styles.subheading}>
                View the journey of your data from source to destination. Click on any Flow Origin
                to view all flows originating from it. Zoom into any resource and view its upstream
                and downstream connections.
              </div>
            </div>
            <Link className="undecorated-link" to={`${ROUTE_CONNECT}/flows/select-type`}>
              <Button buttonTheme="brand" variant="contained" rootStyle={styles.headingCTA}>
                Create New Flow
              </Button>
            </Link>
          </Paper>
        )}
        {withHeader === false && isProjectPipelinesView && (
          <PipelinesListProjectHeader
            length={flowsCount}
            project={selectedProject}
            isDataFlowsLoading={isLoadingProjectById}
          />
        )}
        {showExpandPipelinesForProject ? (
          <>
            <ExpandedPipelines
              pipelines={pipelines}
              children={
                <Box
                  gap={2}
                  flex={1}
                  width="100%"
                  alignItems="center"
                  display="flex"
                  justifyContent="space-between"
                >
                  {SearchBarComponent}
                  {SortSelect}
                  {Pagination}
                  <ExpandController
                    visible
                    expanded={showExpandPipelinesForProject}
                    onChange={(value) => this.setState({ showExpandPipelinesForProject: value })}
                  />
                </Box>
              }
            />
            {/* Flow Detail View Container for Expanded View */}
            {this.props.selectedCardID && (
              <FlowDetailViewContainer
                toggleCloneFlowModal={this._toggleCloneFlowModal}
                key={this.props.selectedCardID}
              />
            )}
          </>
        ) : (
          <section className="view-pipelines">
            <div className="list-controls">
              {SearchBarComponent}
              <div className="control-buttons">
                {SortSelect}
                {Pagination}
                <ExpandController
                  visible={isProjectPipelinesView}
                  expanded={showExpandPipelinesForProject}
                  onChange={(expand) => {
                    this.setState({ showExpandPipelinesForProject: expand });
                  }}
                />
              </div>
            </div>
            {!isEmpty(pipelines) && (
              <BulkActionsBar
                sources={pipelines}
                selectedIds={this.state.selectedFlowIds}
                assignContextView="uncategorized_pipelines"
                onSelectAll={this._selectAllFlows}
                onDeselectAll={this._clearFlowSelection}
                onActionComplete={this._handleBulkActionComplete}
              />
            )}
            <div
              className={`view-filter-pipeline-results ${isEmpty(pipelines) && !isLoading ? 'no-pipelines' : ''}`}
              style={!isEmpty(pipelines) && !isLoading ? styles.mainContent : {}}
            >
              <div className="content-container">
                {!isEmpty(projects) && !disableProjectView && (
                  <div className="project-list">{map(projects, this._renderProjectOrigin)}</div>
                )}

                {(pipelines.length > 0 || isLoading) && (
                  <div className="sources-list">
                    <PipelinesList
                      ref={(ref) => {
                        this.listRef = ref;
                      }}
                      isLoading={isLoading && !this.props.selectedCardID}
                      onClickExpandFlow={this.onExpandFlow}
                      isOldDesign={withHeader === true}
                      _handleSourceClicked={this._handleSourceClicked}
                      isRefreshingPipeline={this.props.isRefreshingPipeline}
                      selectedDataSource={this.state.selectedDataSource}
                      scaleFactor={this.state.scaleFactor}
                      _sourcePipelineElement={this._sourcePipelineElement}
                      sources={pipelines}
                      onRootRef={this._onRootRef}
                      _toggleCloneFlowModal={this._toggleCloneFlowModal}
                      _handleScaleFactorChanged={this._handleScaleFactorChanged}
                      refreshPipeline={this._handleRefreshPipeline}
                      _handleCloseSourcePipeline={this._handleCloseSourcePipeline}
                      toggleUseExpanded={this.toggleUseExpanded}
                      projectId={
                        match?.params?.projectId ? Number(match.params.projectId) : undefined
                      }
                      onItemCheckboxChange={this._toggleFlowSelected}
                      isItemCheckboxChecked={this._getIsFlowSelected()}
                    />
                  </div>
                )}
                {/* Flow Detail View Container */}
                {this.props.selectedCardID && (
                  <FlowDetailViewContainer
                    toggleCloneFlowModal={this._toggleCloneFlowModal}
                    key={this.props.selectedCardID}
                  />
                )}
                {noContentView}

                {/* Project Detail View Container */}
                {!!this.state.selectedProject.id &&
                  [PROJECT_DETAIL_VIEW, PROJECT_DETAIL_VIEW_ACCESS_TAB].includes(
                    this.state.selectedProject.type,
                  ) && (
                    <ProjectDetailViewContainer
                      type={this.state.selectedProject.type}
                      id={this.state.selectedProject.id}
                      onClose={this._handleProjectClicked}
                    />
                  )}

                {/* Project Modal */}
                {/* TODO: Remove this and related reducer code if no longer needed */}
                {/*{this.props.projectSelectedFlow.id && (*/}
                {/*  <ModalProjectActionContainer />*/}
                {/*)}*/}

                {/* Clone Flow Modal */}
                {!!this.state.cloneSelectedFlow && (
                  <ModalCloneFlowContainer
                    data={this.state.cloneSelectedFlow}
                    toggleCloneFlowModal={this._toggleCloneFlowModal}
                    setSelectedPipeline={this._setSelectedPipeline}
                  />
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    );
  }

  _renderProjectOrigin(project, index) {
    const { withHeader } = this.props;
    const { selectedProject } = this.state;
    const flows = this._getProjectFlows(project);
    if (flows.length < 1) return null;

    return (
      <div key={`PROJECT-${index}`} style={styles.flex}>
        <div
          className="icon-container"
          style={{ height: 48, display: 'flex', alignItems: 'center' }}
        >
          <FolderIcon viewBox="1 0 24 24" style={styles.projectIcon} />
        </div>
        <div
          className="content-container"
          style={{
            ...styles.flex,
            ...styles.flexGrow,
            ...styles.overflowHidden,
          }}
        >
          {!!(
            selectedProject.id &&
            selectedProject.id === project.id &&
            selectedProject.type === TOGGLE_PROJECT_PIPELINES
          ) && (
            <div className="project-list-item">
              {flows.length > 0 ? (
                <PipelinesList
                  ref={(ref) => {
                    this.listRef = ref;
                  }}
                  key={flows}
                  disableVirtualization
                  isOldDesign={withHeader === true}
                  projectId={project.id}
                  _handleSourceClicked={this._handleSourceClicked}
                  isRefreshingPipeline={this.props.isRefreshingPipeline}
                  selectedDataSource={this.state.selectedDataSource}
                  scaleFactor={this.state.scaleFactor}
                  _sourcePipelineElement={this._sourcePipelineElement}
                  sources={flows}
                  onRootRef={this._onRootRef}
                  _toggleCloneFlowModal={this._toggleCloneFlowModal}
                  _handleScaleFactorChanged={this._handleScaleFactorChanged}
                  _handleRefreshPipeline={this._handleRefreshPipeline}
                  _handleCloseSourcePipeline={this._handleCloseSourcePipeline}
                  toggleUseExpanded={this.toggleUseExpanded}
                />
              ) : (
                <div className="no-project-data-flows">No Data Flows</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  toggleUseExpanded = () => {
    this.setState({ expanded: !this.state.expanded });

    this.props.saveHashedState('flows.useExpanded', !this.state.expanded);
  };

  _onRootRef = (instance) => {
    if (instance && instance !== this._sourcePipelineElement) {
      this._sourcePipelineElement = instance;
      this.setState({ _forceRerender: Date.now() });
    }
  };

  _setSelectedPipeline(flowNodeId) {
    this.setState({ selectedDataSource: flowNodeId });
  }

  // --- Bulk selection (origin_node_id keyed) -------------------------------
  _toggleFlowSelected = (originNodeId) => {
    if (originNodeId == null) return;
    this.setState((prev) => {
      const next = new Set(prev.selectedFlowIds);
      if (next.has(originNodeId)) {
        next.delete(originNodeId);
      } else {
        next.add(originNodeId);
      }
      return { selectedFlowIds: next };
    });
  };

  // Returns a stable predicate whose identity only changes when the selection
  // Set changes, so the memoized PipelinesList re-renders on selection changes
  // but not on unrelated parent re-renders (e.g. polling ticks).
  _getIsFlowSelected = () => {
    if (this._isFlowSelectedSet !== this.state.selectedFlowIds) {
      this._isFlowSelectedSet = this.state.selectedFlowIds;
      this._isFlowSelectedFn = (originNodeId) => this.state.selectedFlowIds.has(originNodeId);
    }
    return this._isFlowSelectedFn;
  };

  _selectAllFlows = (ids) => {
    this.setState({ selectedFlowIds: new Set(ids) });
  };

  _clearFlowSelection = () => {
    if (this.state.selectedFlowIds.size) {
      this.setState({ selectedFlowIds: new Set() });
    }
  };

  _handleBulkActionComplete = () => {
    this._loadPipelines();
    this._refreshProject();
  };

  _toggleCloneFlowModal(data = null) {
    this.setState({
      cloneSelectedFlow: data,
    });
  }

  _setTimer() {
    if (this._timer) {
      clearInterval(this._timer);
    }

    // Use PIPELINE_LIST preset interval (30s) for dashboard refresh
    this._timer = setInterval(this._handleTick, POLLING_PRESET_INTERVALS.PIPELINE_LIST);
  }

  _clearTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  _handleVisibilityChange() {
    if (document.hidden) {
      // Tab is hidden - pause polling to reduce server load
      this._clearTimer();
    } else {
      // Tab is visible - resume polling and trigger immediate refresh
      if (this.state.selectedDataSource) {
        this._setTimer();
        // Trigger immediate refresh when tab becomes visible
        this._handleTick();
      }
    }
  }

  _handleScaleFactorChanged(scaleFactor) {
    this.setState({ scaleFactor });
  }

  _handleProjectClicked(event, projectID, type) {
    event.stopPropagation();
    const { selectedProject } = this.state;
    const bool = type === selectedProject.type && selectedProject.id === projectID;
    this.setState({
      selectedProject: {
        id: bool ? null : projectID,
        type: bool ? null : type,
      },
    });
  }

  _getFlowObj(flowNodeId) {
    const { entities } = this.props;
    const { flowNodes } = entities;

    return getResourceEntity(flowNodes[flowNodeId], entities);
  }

  _getProjectFlows(project) {
    const { projectDataFlowMapping } = this.props;
    let sources = [];

    if (has(projectDataFlowMapping, project.id) && !isEmpty(projectDataFlowMapping[project.id])) {
      sources = map(projectDataFlowMapping[project.id], this._getFlowObj);
    }

    return sources;
  }

  _handleSourceClicked(source) {
    const { selectedDataSource } = this.state;
    const isCollapsing = !!selectedDataSource && source.nodeKey === selectedDataSource;

    // Toggle immediately so a collapse click is as responsive as expand.
    // Previously collapse deferred the state change by 1s to run a shrink
    // animation, which made the click feel like it did nothing.
    this.setState(
      {
        selectedDataSource: isCollapsing ? null : source?.flow_node_id,
      },
      () => {
        this.props.saveHashedState('flows.viewport.flow_nodes_all', null);
        this.props.saveHashedState('flows.selected', this.state.selectedDataSource);
      },
    );
  }

  onExpandFlow = (flowId) => {
    const flow = this.props.flowNodes?.[flowId];
    if (flow?.flow_type === 'rag') {
      this.props.history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/${flowId}/edit`);
    } else {
      this.props.history.push(
        `${ROUTE_CONNECT}${ROUTE_PIPELINES}/${flow?.flow_type}/${flowId}/edit`,
      );
    }
    // this.toggleUseExpanded();
    // const searchSeg = {
    //   token: SearchFilterTypes.ID,
    //   value: `${flowId}`,
    //   segment: `${SearchFilterTypes.ID}:${flowId}`
    // };
    // const currentSegments = this.searchBar.getSegments();
    // this.searchBar.focus();
    // this.searchBar.setSegments([
    //   ...currentSegments.filter((s) => s.token !== SearchFilterTypes.ID),
    //   searchSeg
    // ]);
    // this.searchBar.blur();
  };

  _loadMetrics(options = {}) {
    const { requestFlowMetrics, metricsInterval } = this.props;
    const { selectedDataSource } = this.state;
    if (!selectedDataSource) return;
    const flow = this._getFlowObj(selectedDataSource);
    const sourceId = flow?.data_source_id ?? null;
    const interval = options.interval ?? metricsInterval ?? FLOW_METRICS_INTERVAL_LATEST_RUN;
    requestFlowMetrics(selectedDataSource, sourceId, { ...options, interval });
  }

  _handleSearchSuggestionSelected(flowNodeId) {
    const { entities, pipelines, setDetailViewCard } = this.props;
    const { flowNodes } = entities;
    const selectedDataSource = getResourceEntity(flowNodes[flowNodeId], entities);

    this.setState({ selectedDataSource: selectedDataSource.flow_node_id });
    setDetailViewCard(selectedDataSource);

    const index = pipelines.findIndex((pipeline) => pipeline && pipeline.id === flowNodeId);
    this.scrollToIndex(index);
  }

  scrollToIndex = (index) => {
    setTimeout(() => {
      if (this.listRef) {
        this.listRef.scrollToIndex({
          index,
          align: 'start',
          offset: -60,
        });
      }
    }, 100);
  };

  _handleSortCriteriaChanged(selectedOption) {
    const value = getNewSelectionValue(selectedOption, true, '');
    this.props.setSortCriteria(value);
    this.setState({ selectedSortOption: selectedOption });
  }

  _handleCloseSourcePipeline() {
    this.setState({
      selectedDataSource: null,
    });
  }

  _handleRefreshPipeline() {
    const { selectedDataSource } = this.state;
    const { isRefreshingPipeline, refreshPipeline, pipelines } = this.props;

    if (this.state.expanded && !selectedDataSource) return;

    //To prevent requesting for a deleted flow, we will check if the source exists
    const sourceExists = pipelines?.find((p) => p.flow_node_id === selectedDataSource);

    if (selectedDataSource && sourceExists && !isRefreshingPipeline) {
      // Structure reload only. The canvas Refresh button re-fetches per-node
      // metrics itself (FlowNodes `fetchFlowMetrics`, interval-aware); calling
      // `_loadMetrics()` here too made Refresh fire the metrics call twice
      // (NEX-21823). Auto-poll metrics is refreshed by `_handleTick` below.
      refreshPipeline(selectedDataSource);
    }
  }

  _handleTick() {
    if (this.props.activeViews?.formViewOpen) return; // if the form view is open, skip the refresh pipeline
    if (this.props.activeViews?.detailPanelOpen) return; // if the detail panel is open, skip — resume when panel closes
    if (this.props.activeViews?.resourceEditModalOpen) return; // DAG edit modal open — skip refresh
    this._handleRefreshPipeline(false);
    // The 30s poll refreshes per-node metrics too (the manual Refresh button does
    // this via FlowNodes; the timer has no button click, so fetch here).
    this._loadMetrics();
  }

  async _loadPipelines() {
    const {
      match,
      loadPipelines,
      setTotalPages,
      setTotalItems,
      getCredentialsGroups,
      currentPage,
    } = this.props;

    const response = await loadPipelines({
      isPreview: false,
      projectId: match.params?.projectId,
      page: this.props.listingPageForRequest,
    });

    if (response?.totalPages) {
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    }
    return response;
  }

  _refreshProject() {
    const { match, getProjectById } = this.props;
    const projectId = match?.params?.projectId;

    if (projectId && getProjectById) {
      // Update the specific project's summary/count without reloading the whole list
      getProjectById(projectId).catch((e) => {
        console.log('error refreshing project by id', e);
      });
    }
  }

  getProjectsEntities = () => {
    const { projects } = this.props?.entities || {};
    return projects;
  };

  getProjectContext = () => {
    const { match } = this.props;
    const projects = this.getProjectsEntities();
    return match?.params?.projectId && projects[match.params.projectId];
  };

  /**
   * Checks if the request has completed by checking
   *  if the request key is in the previous props and not in the current props
   */
  hasRequestCompleted(prevProps, requestKey) {
    const { flowOperationRequestFlags } = this.props;
    return (
      prevProps.flowOperationRequestFlags?.[requestKey] && !flowOperationRequestFlags?.[requestKey]
    );
  }

  _checkForFlowOperations(prevProps) {
    const { isSQLAutomations } = this.props;
    const isProjectPipelinesView = !!this.getProjectContext();
    const isSqlAutomationsView = !!isSQLAutomations;

    if (!isProjectPipelinesView && !isSqlAutomationsView) return;

    const operations = [
      {
        // Updates count after flow is deleted
        requestKey: 'DELETE_PIPELINE_REQUEST',
        actions: () => {
          if (isSqlAutomationsView) {
            this._loadPipelines();
            return;
          }
          this._refreshProject();
        },
      },
      {
        // Updates count after flow is removed from project
        requestKey: 'REMOVE_FLOW_FROM_PROJECT_REQUEST',
        actions: () => this._refreshProject(),
      },
      {
        // Reloads flow list to show new clone AND updates project count
        requestKey: 'CLONE_FLOW_REQUEST',
        actions: () => {
          this._loadPipelines();
          this._refreshProject();
        },
      },
    ];

    operations.forEach((operation) => {
      if (this.hasRequestCompleted(prevProps, operation.requestKey)) {
        operation.actions();
      }
    });
  }
}

Pipelines.defaultProps = {
  disableProjectView: false,
  withHeader: true,
};

export default Pipelines;
